/*
 * Keeps add-on cart lines consistent with the product lines they belong to.
 *
 * Data model:
 *   product line -> visible property  "Add ons": comma separated add-on names
 *   add-on line  -> visible property  "Applies to": product and variant name
 *                   hidden  property  "_addon_for": product variant id
 *
 * Shopify only merges two cart lines when the variant AND the properties match,
 * so each product the customer opts in for gets its own add-on lines. Nothing
 * is ever applied cart-wide.
 */
(function () {
  var PROP_LINK = '_addon_for';
  var PROP_LIST = 'Add ons';
  var busy = false;

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json();
    });
  }

  function labelsOn(line) {
    var raw = (line.properties || {})[PROP_LIST];
    if (!raw) return [];
    return raw.split(',').map(function (part) {
      return part.trim();
    }).filter(Boolean);
  }

  /* A cart can hold the same variant twice, once with add-ons and once without.
     Prefer the line that actually names this add-on before falling back to a
     plain variant match, so quantities sync against the right line. */
  function findParent(parents, addon) {
    var linkedTo = String(addon.properties[PROP_LINK]);
    var sameVariant = parents.filter(function (candidate) {
      return String(candidate.variant_id) === linkedTo;
    });

    var named = sameVariant.filter(function (candidate) {
      return labelsOn(candidate).indexOf(addon.product_title) !== -1;
    });

    return named[0] || sameVariant[0] || null;
  }

  function planChanges(cart) {
    var parents = [];
    var addons = [];

    (cart.items || []).forEach(function (item) {
      if ((item.properties || {})[PROP_LINK]) {
        addons.push(item);
      } else {
        parents.push(item);
      }
    });

    var operations = [];
    var surviving = {};

    addons.forEach(function (addon) {
      var parent = findParent(parents, addon);

      if (!parent) {
        // The product this add-on belonged to is gone.
        operations.push({ id: addon.key, quantity: 0 });
        return;
      }

      if (!surviving[parent.key]) surviving[parent.key] = [];
      surviving[parent.key].push(addon.product_title);

      if (parent.quantity !== addon.quantity) {
        // Add-ons are priced per item, so they track the product's quantity.
        operations.push({ id: addon.key, quantity: parent.quantity });
      }
    });

    // Rebuild each product's "Add ons" label from the add-on lines that remain,
    // so a customer removing one add-on by hand does not leave a false label.
    parents.forEach(function (parent) {
      var current = labelsOn(parent).join(', ');
      var desired = (surviving[parent.key] || []).join(', ');

      if (current === desired) return;

      var properties = {};
      Object.keys(parent.properties || {}).forEach(function (key) {
        if (key !== PROP_LIST) properties[key] = parent.properties[key];
      });
      if (desired) properties[PROP_LIST] = desired;

      operations.push({ id: parent.key, properties: properties });
    });

    return operations;
  }

  function reconcile() {
    if (busy) return Promise.resolve(false);
    busy = true;

    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var operations = planChanges(cart);

        if (!operations.length) return false;

        return operations
          .reduce(function (chain, operation) {
            return chain.then(function () {
              return postJSON('/cart/change.js', operation);
            });
          }, Promise.resolve())
          .then(function () {
            return true;
          });
      })
      .catch(function (error) {
        console.error('[product-addons] reconcile failed:', error);
        return false;
      })
      .then(function (changed) {
        busy = false;

        if (changed) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }

        return changed;
      });
  }

  document.addEventListener('cart:change', function () {
    reconcile();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      reconcile();
    });
  } else {
    reconcile();
  }
})();
